import { cn } from '../cn.js';
import { useDrawIn } from './internal/useDrawIn.js';

export interface ProgressBarProps {
  /** 0–100. Values outside that range are clamped rather than producing an invalid bar width. */
  value: number;
  /** A token reference, e.g. "var(--accent)" — never a literal colour. */
  color?: string;
  label?: string;
  /** Shows the rounded percentage after the label, e.g. "62%". */
  showValue?: boolean;
  /** Overrides the generic "Progress" aria-label used when `label` is absent — for a caller that
   *  renders several bars on one page and needs each one distinguishable to a screen reader
   *  without also rendering a visible label above the bar. */
  ariaLabel?: string;
  className?: string;
}

const DRAW_IN_DURATION = '600ms';

/**
 * Linear progress — a key result's current/target reading, a goal's rollup, an import job's
 * status. `Ring`'s straight-line sibling: same 0–100 contract and clamping behaviour, different
 * shape for contexts (a table row, a compact card) where a circle doesn't fit.
 *
 * Draws in from empty the first time it scrolls into view (`useDrawIn`, same mechanism `Ring`/
 * `BarChart`/`DonutChart` use) rather than snapping straight to `value` on mount.
 */
export function ProgressBar({
  value,
  color = 'var(--accent)',
  label,
  showValue = false,
  ariaLabel,
  className,
}: ProgressBarProps) {
  const [drawInRef, revealed] = useDrawIn<HTMLDivElement>();
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <div ref={drawInRef} className={cn('flex flex-col gap-1', className)}>
      {(label || showValue) && (
        <div className="flex items-center justify-between gap-2 text-xs text-fg-muted">
          {label && <span>{label}</span>}
          {showValue && <span className="tabular-nums">{Math.round(clamped)}%</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ? undefined : (ariaLabel ?? 'Progress')}
        className="h-2 w-full overflow-hidden rounded-full bg-bg-subtle"
      >
        <div
          className="h-full rounded-full"
          style={{
            width: revealed ? `${clamped}%` : '0%',
            backgroundColor: color,
            transition: `width ${DRAW_IN_DURATION} var(--ease, ease)`,
          }}
        />
      </div>
    </div>
  );
}
